import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useNavigate, useParams} from 'react-router-dom';
import Button from '../../../../uiToolkit/Buttons/Button';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import MicroreelsListStore from '../store/MicroreelsListStore';
import {LearnerListItem} from '../../../../data/models/Learners/Learner';
import LearnersTable from '../../../../modules/LearnersTable/LearnersTable';
import LearnersEmpty from '../../../../modules/LearnersTable/LearnersEmpty';
import LearnersSearch from '../../../../modules/LearnersTable/LearnersSearch';
import {useDebouncedSearch} from '../../../../utils/useDebouncedSearch';
import {microreelsListText} from '../microreelsListText';
import AudienceToggle, {
  Audience,
} from '../../../../modules/LearnerGroups/AudienceToggle';
import GroupsPicker from '../../../../modules/LearnerGroups/GroupsPicker';
import LearnerGroupsStore from '../../../../modules/LearnerGroups/store/LearnerGroupsStore';
import styles from './MicroreelLearnersPanel.module.css';

interface Props {
  publishedCourseId: string;
  // Without it the footer has no Close button (the panel is embedded in a page).
  onClose?: () => void;
  microreelsListStore?: MicroreelsListStore;
  learnerGroupsStore?: LearnerGroupsStore;
}

const ADD_TAB = 'add';
const ENROLLED_TAB = 'enrolled';
const PAGE_SIZE = 10;

/** Enrolled / Add learners tabs for a published microreel group, with groups. */
const MicroreelLearnersPanel = inject(
  'microreelsListStore',
  'learnerGroupsStore',
)(
  observer(
    ({
      publishedCourseId,
      onClose,
      microreelsListStore,
      learnerGroupsStore,
    }: Props) => {
      const {organizationId} = useParams();
      const navigate = useNavigate();

      // Sharing usually means adding people, so the panel opens on Add
      // learners; the list effect below loads whichever tab is showing.
      const [tab, setTab] = useState(ADD_TAB);
      const [audience, setAudience] = useState<Audience>('learners');
      const [checkedGroups, setCheckedGroups] = useState<string[]>([]);
      const [learners, setLearners] = useState<LearnerListItem[]>([]);
      const [pages, setPages] = useState(0);
      const [page, setPage] = useState(1);
      const [search, setSearch] = useState('');
      const [checked, setChecked] = useState<string[]>([]);
      const [enrolledTotal, setEnrolledTotal] = useState(0);
      const [submitting, setSubmitting] = useState(false);
      const [fetchKey, setFetchKey] = useState(0);
      // The list's own loading: the store's flag also follows the badge count, which can
      // finish first and show the empty state before the list arrives.
      const [listLoading, setListLoading] = useState(true);

      const debouncedSearch = useDebouncedSearch(search);
      const isEnrolledTab = tab === ENROLLED_TAB;

      useEffect(() => {
        if (!organizationId) return;
        let active = true;
        setListLoading(true);
        const request = isEnrolledTab
          ? microreelsListStore!.getEnrolledLearners(
              organizationId,
              publishedCourseId,
              page - 1,
              PAGE_SIZE,
              debouncedSearch,
            )
          : microreelsListStore!.getEnrollmentCandidates(
              organizationId,
              publishedCourseId,
              page - 1,
              PAGE_SIZE,
              debouncedSearch,
            );
        request.then(res => {
          if (!active) return;
          setLearners(res.data);
          setPages(res.pagesCount);
          setListLoading(false);
        });
        return () => {
          active = false;
        };
      }, [
        organizationId,
        publishedCourseId,
        tab,
        page,
        debouncedSearch,
        fetchKey,
      ]);

      useEffect(() => {
        if (!organizationId) return;
        let cancelled = false;
        microreelsListStore!
          .getEnrolledLearners(organizationId, publishedCourseId, 0, 1)
          .then(res => {
            if (!cancelled) {
              setEnrolledTotal(res.pagesCount);
            }
          });
        return () => {
          cancelled = true;
        };
      }, [organizationId, publishedCourseId, fetchKey]);

      useEffect(() => {
        setPage(1);
        setChecked([]);
      }, [debouncedSearch]);

      useEffect(() => {
        if (organizationId) {
          learnerGroupsStore!.fetchLearnerGroupNames(organizationId);
        }
      }, [organizationId]);

      const switchTab = (next: string) => {
        // The open tab keeps its list and ticks: nothing would load the cleared list again.
        if (next === tab) return;
        setTab(next);
        setPage(1);
        setSearch('');
        setChecked([]);
        setCheckedGroups([]);
        setAudience('learners');
        setLearners([]);
      };

      const isGroupsAudience = audience === 'groups';

      const filteredGroups = learnerGroupsStore!.groups.filter(group =>
        group.name.toLowerCase().includes(debouncedSearch.toLowerCase()),
      );

      const onToggleGroup = (groupId: string) => {
        setCheckedGroups(current =>
          current.includes(groupId)
            ? current.filter(id => id !== groupId)
            : [...current, groupId],
        );
      };

      const onToggle = (learnerId: string) => {
        setChecked(current =>
          current.includes(learnerId)
            ? current.filter(id => id !== learnerId)
            : [...current, learnerId],
        );
      };

      const onToggleAll = () => {
        const allChecked =
          learners.length > 0 &&
          learners.every(learner => checked.includes(learner.learnerId));
        setChecked(
          allChecked ? [] : learners.map(learner => learner.learnerId),
        );
      };

      const onConfirm = async () => {
        if (!organizationId || !checked.length || submitting) return;
        setSubmitting(true);
        try {
          const success = isEnrolledTab
            ? await microreelsListStore!.removeLearnersFromGroup(
                organizationId,
                publishedCourseId,
                checked,
              )
            : await microreelsListStore!.enrollLearners(
                organizationId,
                publishedCourseId,
                checked,
              );
          setChecked([]);
          if (success) {
            setPage(1);
          }
          setFetchKey(key => key + 1);
        } finally {
          setSubmitting(false);
        }
      };

      const onConfirmGroups = async () => {
        if (!organizationId || !checkedGroups.length || submitting) return;
        setSubmitting(true);
        try {
          const success = await learnerGroupsStore!.enrollGroups(
            organizationId,
            checkedGroups,
            publishedCourseId,
          );
          setCheckedGroups([]);
          if (success) {
            setChecked([]);
            setAudience('learners');
          }
          setFetchKey(key => key + 1);
        } finally {
          setSubmitting(false);
        }
      };

      const isLoading = microreelsListStore!.isEnrolledLoading;

      const selectedCount = (isGroupsAudience ? checkedGroups : checked).length;
      const confirmTitle = `${
        isEnrolledTab
          ? microreelsListText.removeSelected
          : microreelsListText.enrollSelected
      }${selectedCount ? ` (${selectedCount})` : ''}`;

      const renderEmpty = () => {
        if (debouncedSearch) {
          return (
            <LearnersEmpty
              title={microreelsListText.emptySearchTitle}
              text={microreelsListText.emptySearchText}
              actionTitle={microreelsListText.clearSearch}
              onAction={() => {
                setPage(1);
                setSearch('');
              }}
            />
          );
        }
        if (isEnrolledTab) {
          return (
            <LearnersEmpty
              title={microreelsListText.emptyEnrolledTitle}
              text={microreelsListText.emptyEnrolledText}
              actionTitle={microreelsListText.addLearnersTab}
              onAction={() => switchTab(ADD_TAB)}
            />
          );
        }
        return (
          <LearnersEmpty
            title={microreelsListText.emptyNoCandidatesTitle}
            text={microreelsListText.emptyNoCandidatesText}
            actionTitle={microreelsListText.inviteToWorkspace}
            onAction={() => navigate(`/manage/${organizationId}/learners`)}
          />
        );
      };

      return (
        <div className={styles.panel}>
          <div className={styles.tabsRow}>
            <div className={styles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={isEnrolledTab}
                className={`${styles.tab} ${
                  isEnrolledTab ? styles.tabActive : ''
                }`}
                onClick={() => switchTab(ENROLLED_TAB)}>
                {microreelsListText.enrolledLearnersTab}
                <span className={styles.badge}>{enrolledTotal}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!isEnrolledTab}
                className={`${styles.tab} ${
                  !isEnrolledTab ? styles.tabActive : ''
                }`}
                onClick={() => switchTab(ADD_TAB)}>
                {microreelsListText.addLearnersTab}
              </button>
            </div>

            <div className={styles.searchRow}>
              {!isEnrolledTab && (
                <AudienceToggle audience={audience} onChange={setAudience} />
              )}
              <LearnersSearch value={search} onChange={setSearch} />
            </div>
          </div>

          <div className={styles.body}>
            {isGroupsAudience ? (
              <GroupsPicker
                groups={filteredGroups}
                checked={checkedGroups}
                search={debouncedSearch}
                onToggle={onToggleGroup}
              />
            ) : listLoading && !learners.length ? (
              <div className={styles.loading}>
                <LoadingSpinner size={48} />
              </div>
            ) : learners.length ? (
              <LearnersTable
                learners={learners}
                checked={checked}
                page={page}
                pages={pages}
                onToggle={onToggle}
                onToggleAll={onToggleAll}
                onPageChange={setPage}
                groupNames={
                  isEnrolledTab
                    ? learnerGroupsStore!.learnerGroupNames
                    : undefined
                }
              />
            ) : (
              renderEmpty()
            )}
          </div>

          <div className={styles.footer}>
            <span className={styles.status}>
              {isGroupsAudience
                ? checkedGroups.length
                  ? microreelsListText.selectedCount(checkedGroups.length)
                  : microreelsListText.selectGroupsToEnroll
                : checked.length
                ? microreelsListText.selectedCount(checked.length)
                : isEnrolledTab
                ? microreelsListText.enrolledFooter(enrolledTotal)
                : microreelsListText.selectToEnroll}
            </span>
            <div className={styles.actions}>
              {onClose && (
                <Button
                  title={microreelsListText.close}
                  uiType="text"
                  onClick={onClose}
                />
              )}
              <Button
                title={confirmTitle}
                uiType={isEnrolledTab ? 'action_danger' : 'action'}
                onClick={isGroupsAudience ? onConfirmGroups : onConfirm}
                disabled={
                  isGroupsAudience
                    ? !checkedGroups.length
                    : !checked.length || isLoading
                }
                isLoading={submitting}
              />
            </div>
          </div>
        </div>
      );
    },
  ),
);

export default MicroreelLearnersPanel;
